import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtApprovedDate = (iso) => {
  if (!iso) return '—';
  return String(iso).slice(0, 10);
};

// عمود "أيام التأخير": لون حسب العتبة (7 أصفر، 14 برتقالي، >=14 أحمر)
const DaysDelayedBadge = ({ days }) => {
  if (days == null) return <span className="text-gray-400">—</span>;
  const cls = days >= 14 ? 'bg-red-600 text-white' : days >= 7 ? 'bg-orange-500 text-white' : 'bg-yellow-300 text-yellow-900';
  return <span className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}>{days}</span>;
};

// ===================================================================
export function PrintReportView({ token, onClose, filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // بناء سلسلة الاستعلام من الفلاتر
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.supplier) p.append('supplier', filters.supplier);
    if (filters.project) p.append('project', filters.project);
    if (filters.approver_id) p.append('approver_id', filters.approver_id);
    if (filters.creator_id) p.append('creator_id', filters.creator_id);
    if (filters.date_from) p.append('date_from', filters.date_from);
    if (filters.date_to) p.append('date_to', filters.date_to);
    return p.toString();
  }, [filters]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const url = `${API_URL}/api/invoice-matching/report/unlinked${queryString ? '?' + queryString : ''}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setData(await res.json());
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, queryString]);

  const handlePrint = () => window.print();

  // اسم المعتمِد المختار للعرض (نأخذه من قائمة المعتمِدين الراجعة من الـ API)
  const selectedApproverName = useMemo(() => {
    if (!filters.approver_id || !data?.available_approvers) return '';
    const found = data.available_approvers.find((a) => a.id === filters.approver_id);
    return found ? found.name : filters.approver_id;
  }, [filters.approver_id, data]);

  // اسم مدخل الكشف المختار للعرض
  const selectedCreatorName = useMemo(() => {
    if (!filters.creator_id || !data?.available_creators) return '';
    const found = data.available_creators.find((c) => c.id === filters.creator_id);
    return found ? found.name : filters.creator_id;
  }, [filters.creator_id, data]);

  // تطبيق الفلاتر العميل (min_days + sort_by) على القائمة
  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    let items = data.items;
    const md = parseInt(filters.min_days, 10);
    if (md && md > 0) {
      items = items.filter((it) => it.days_delayed != null && it.days_delayed >= md);
    }
    const sortBy = filters.sort_by || 'default';
    if (sortBy === 'default') return items;
    const arr = [...items];
    const dir = sortBy === 'days_desc' ? -1 : 1;
    arr.sort((a, b) => {
      const da = a.days_delayed, db = b.days_delayed;
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return (da - db) * dir;
    });
    return arr;
  }, [data, filters.min_days, filters.sort_by]);

  // شرائح الفلاتر المعروضة
  const filterChips = [];
  if (filters.supplier) filterChips.push({ label: 'المورد', value: filters.supplier });
  if (filters.project) filterChips.push({ label: 'المشروع', value: filters.project });
  if (selectedCreatorName) filterChips.push({ label: 'مدخل الكشف', value: selectedCreatorName });
  if (selectedApproverName) filterChips.push({ label: 'المشرف', value: selectedApproverName });
  if (filters.date_from) filterChips.push({ label: 'من', value: filters.date_from });
  if (filters.date_to) filterChips.push({ label: 'إلى', value: filters.date_to });
  if (filters.min_days) filterChips.push({ label: 'تأخير ≥', value: `${filters.min_days} يوم` });
  if (filters.sort_by === 'days_desc') filterChips.push({ label: 'الترتيب', value: 'الأكثر تأخيراً أولاً ▼' });
  if (filters.sort_by === 'days_asc') filterChips.push({ label: 'الترتيب', value: 'الأقل تأخيراً أولاً ▲' });

  const content = (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" dir="rtl" data-print-root>
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { height: auto !important; overflow: visible !important; }
          body > *:not([data-print-root]) { display: none !important; }
          [data-print-root] { position: static !important; width: 100% !important; height: auto !important; overflow: visible !important; display: block !important; background: white !important; }
        }
      `}</style>
      <div className="print:hidden bg-gray-50 border-b px-4 py-3 flex items-center gap-3 sticky top-0">
        <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-gray-200 flex items-center justify-center" data-testid="close-print-report-btn">
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 font-bold">معاينة تقرير الاستلامات بدون فواتير</h2>
        <button onClick={handlePrint} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-semibold" data-testid="print-report-action-btn">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-4">
        <div className="text-center mb-4 print:mb-3">
          <h1 className="text-2xl print:text-xl font-bold">تقرير الاستلامات المعتمدة بدون فواتير</h1>
          <p className="text-sm text-gray-600 mt-2">تاريخ الطباعة: {new Date().toLocaleDateString('en-US')}</p>
        </div>

        {/* شرائح الفلاتر المُطبَّقة */}
        {filterChips.length > 0 && (
          <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 mb-4 text-sm" data-testid="print-filters-summary">
            <div className="font-bold text-purple-900 mb-2">الفلاتر المُطبَّقة:</div>
            <div className="flex flex-wrap gap-2">
              {filterChips.map((c, i) => (
                <span key={i} className="bg-white border border-purple-300 text-purple-800 px-3 py-1 rounded-full text-xs">
                  <strong>{c.label}:</strong> {c.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
        ) : !data || data.total_unlinked === 0 || filteredItems.length === 0 ? (
          <p className="text-center text-gray-500">لا توجد استلامات بدون فواتير مطابقة للفلاتر المُطبَّقة</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4 text-center">
              <div className="border rounded-xl p-2">
                <div className="text-xl font-bold text-amber-700">{filteredItems.length}{filteredItems.length !== data.total_unlinked ? <span className="text-xs text-gray-500"> من {data.total_unlinked}</span> : ''}</div>
                <div className="text-xs text-gray-500">استلامات بدون فواتير</div>
              </div>
              <div className="border rounded-xl p-2">
                <div className="text-xl font-bold text-purple-700">{data.suppliers_count}</div>
                <div className="text-xs text-gray-500">موردين</div>
              </div>
              <div className="border rounded-xl p-2">
                <div className="text-xl font-bold text-gray-700">{data.users_count}</div>
                <div className="text-xs text-gray-500">مستخدمين</div>
              </div>
            </div>

            <table className="w-full text-sm border-collapse border border-gray-300 print:text-xs">
              <thead className="bg-amber-100">
                <tr>
                  <th className="border border-gray-300 px-2 py-2 text-right">#</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">كشف #</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">رقم الاستلام</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">التاريخ</th>
                  <th className="border border-gray-300 px-2 py-2 text-center bg-red-100">أيام التأخير</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">المورد</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">المشروع</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">المنشئ</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">المعتمِد</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">تاريخ الاعتماد</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((row, i) => {
                  const r = row.receipt || {};
                  return (
                    <tr key={i}>
                      <td className="border border-gray-300 px-2 py-1.5 text-center">{i + 1}</td>
                      <td className="border border-gray-300 px-2 py-1.5 font-bold">#{row.delivery_number}</td>
                      <td className="border border-gray-300 px-2 py-1.5 font-semibold">{r.receipt_number}</td>
                      <td className="border border-gray-300 px-2 py-1.5">{r.receipt_date || '—'}</td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center"><DaysDelayedBadge days={row.days_delayed} /></td>
                      <td className="border border-gray-300 px-2 py-1.5">{row.supplier_name}</td>
                      <td className="border border-gray-300 px-2 py-1.5">{r.project_number || '—'}</td>
                      <td className="border border-gray-300 px-2 py-1.5">{row.created_by}</td>
                      <td className="border border-gray-300 px-2 py-1.5 text-purple-700 font-semibold">{row.approved_by || '—'}</td>
                      <td className="border border-gray-300 px-2 py-1.5">{fmtApprovedDate(row.approved_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
